/**
 * Is this value safe to send to the agent?
 *
 * Every tool result crosses two boundaries before an agent sees it — the app
 * frame's `postMessage` to the bridge, and the WebSocket to the relay — and both
 * carry JSON. This module is the check that runs before either.
 *
 * ## Why this is an ALLOWLIST
 *
 * The obvious implementation is a denylist: look for the things known to break.
 * That is how the bug this module exists to prevent was found in the first place.
 *
 *     JSON.stringify(new Map([[3, "front"]]))   ===   "{}"
 *
 * A `Map` does not throw and does not produce anything malformed. It SILENTLY
 * EMPTIES — so a check of the form "stringify it and see whether that worked"
 * passes a Map, and the value reaches the agent as an empty object: a wrong
 * answer wearing a successful response's clothes. `Set` does the same.
 *
 * A denylist only ever catches the cases someone thought of. So this module
 * inverts it: **only a known-safe shape may cross, and everything else is a
 * named violation.** `Map` and `Set` are then caught by the same clause as a
 * React fiber, a DOM node and the live Mapbox instance — none of which anybody
 * has to remember to enumerate. That is the whole architectural point, and it is
 * why `JSON.stringify` is banned in this directory by an ast-grep rule.
 *
 * ## The contract
 *
 * The check REPORTS; it does not serialise, log, or throw. It returns the first
 * violation depth-first, with the PATH at which it was found and the type name
 * found there, because the error taxonomy this feeds requires messages of the
 * form `Map at result.roles` — a message naming only the type leaves the caller
 * hunting through a nested result for it.
 *
 * One violation, not all of them: a result with a `Map` in it is not shipping
 * either way, and the first one located is the one to fix.
 */

/** What was found. Each value corresponds to a distinct silent failure. */
export type ViolationKind =
  | "map"
  | "set"
  | "circular"
  | "class-instance"
  | "function"
  | "symbol"
  | "bigint"
  | "non-finite-number"
  | "undefined";

export type JsonSafetyViolation = {
  /** Where it was found, e.g. `result.roles` or `result.items[3].bounds`. */
  readonly path: string;
  readonly kind: ViolationKind;
  /** The constructor name where one is available, else the internal tag. */
  readonly typeName: string;
  /** One sentence naming the cause AND what would silently happen without it. */
  readonly message: string;
};

export type JsonSafetyResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly violation: JsonSafetyViolation };

/**
 * Inspect a value for JSON-safety.
 *
 * @param rootLabel how the outermost value is named in violation paths. Defaults
 *   to `result`, so messages read `Map at result.roles` — matching the taxonomy.
 */
export function inspectJsonSafety(value: unknown, rootLabel = "result"): JsonSafetyResult {
  const violation = walk(value, rootLabel, []);
  return violation === null ? { ok: true } : { ok: false, violation };
}

/**
 * `ancestors` holds only the objects on the CURRENT path, not every object seen.
 * That distinction is load-bearing: a value referenced twice from different
 * branches is a DAG, which `JSON.stringify` handles correctly by duplicating it.
 * Only a reference back into the current path is a cycle. A global "seen" set
 * would reject legitimate shared references — a shared config object appearing
 * under two keys is ordinary and safe.
 */
function walk(
  value: unknown,
  path: string,
  ancestors: readonly object[],
): JsonSafetyViolation | null {
  if (value === null) return null;

  switch (typeof value) {
    case "string":
    case "boolean":
      return null;

    case "number":
      // NaN and ±Infinity stringify to `null`. Not an error — a changed value.
      return Number.isFinite(value)
        ? null
        : violation(path, "non-finite-number", String(value), `becomes null in JSON`);

    case "undefined":
      // Deliberately strict. As an object property the key is DROPPED; in an
      // array it becomes `null`. Both are silent shape changes, and this system
      // validates results against declared JSON Schemas — so a tool returning
      // `undefined` for a declared property emits a response that fails its own
      // schema with nothing to say so. Use `null` to mean "no value".
      return violation(
        path,
        "undefined",
        "undefined",
        `is dropped from objects and becomes null in arrays`,
      );

    case "function":
      return violation(
        path,
        "function",
        typeNameOf(value),
        `is dropped from objects and becomes null in arrays`,
      );

    case "symbol":
      return violation(
        path,
        "symbol",
        "Symbol",
        `is dropped from objects and becomes null in arrays`,
      );

    case "bigint":
      return violation(path, "bigint", "BigInt", `makes JSON.stringify throw`);

    default:
      break;
  }

  const object = value as object;

  if (ancestors.includes(object)) {
    return violation(path, "circular", typeNameOf(object), `refers back into its own path`);
  }

  // Named explicitly ONLY so the message can be specific. The allowlist below
  // would reject both anyway, which is the safety property that matters.
  if (object instanceof Map) {
    return violation(
      path,
      "map",
      typeNameOf(object),
      `serialises to {} — its entries vanish silently`,
    );
  }
  if (object instanceof Set) {
    return violation(
      path,
      "set",
      typeNameOf(object),
      `serialises to {} — its members vanish silently`,
    );
  }

  // The one permitted class instance: `Date` has a `toJSON` and becomes an ISO
  // string. Lossy but predictable, and it is the standard JSON idiom.
  if (object instanceof Date) return null;

  const nextAncestors = [...ancestors, object];

  if (Array.isArray(object)) {
    const items = object as readonly unknown[];
    for (let index = 0; index < items.length; index += 1) {
      const found = walk(items[index], `${path}[${index}]`, nextAncestors);
      if (found !== null) return found;
    }
    return null;
  }

  // THE ALLOWLIST. A plain object or a null-prototype bag may cross. Everything
  // else is a class instance — which is how React fibers, DOM nodes, Errors and
  // the live Mapbox instance are all caught without being enumerated.
  const prototype = Object.getPrototypeOf(object) as object | null;
  if (prototype !== null && prototype !== Object.prototype) {
    return violation(
      path,
      "class-instance",
      typeNameOf(object),
      `is a class instance, so JSON keeps only its own enumerable fields`,
    );
  }

  for (const [key, child] of Object.entries(object)) {
    const found = walk(child, childPath(path, key), nextAncestors);
    if (found !== null) return found;
  }
  return null;
}

function violation(
  path: string,
  kind: ViolationKind,
  typeName: string,
  consequence: string,
): JsonSafetyViolation {
  return {
    path,
    kind,
    typeName,
    message: `${typeName} at ${path} is not JSON-safe: it ${consequence}.`,
  };
}

function typeNameOf(value: unknown): string {
  const ctor = (value as { constructor?: unknown }).constructor;
  if (typeof ctor === "function" && ctor.name.length > 0) {
    return ctor.name;
  }
  return Object.prototype.toString.call(value).slice(8, -1);
}

const IDENTIFIER = /^[A-Za-z_$][A-Za-z0-9_$]*$/;

function childPath(parent: string, key: string): string {
  if (IDENTIFIER.test(key)) return `${parent}.${key}`;
  // Quoted by hand rather than with `JSON.stringify`, which this directory
  // forbids. Escaping backslashes before quotes matters, not the other way round.
  const quoted = key.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  return `${parent}["${quoted}"]`;
}
